import { useEffect, useState } from 'react';
import { getSiteSettings, updateSiteSettings, resetSiteSettings } from '@/lib/api';
import { invalidateSettings, pushPreviewSettings, clearPreviewSettings } from '@/lib/useSiteSettings';
import { Field, TextInput, TextArea, ImageUploader } from './fields';

const TABS = [
    { k: 'brand', l: 'Brand', route: '/' },
    { k: 'home', l: 'Home', route: '/' },
    { k: 'about', l: 'About', route: '/about' },
    { k: 'private_viewing', l: 'Private Viewing', route: '/private-viewing' },
    { k: 'artists_page', l: 'Artists page', route: '/artists' },
    { k: 'artworks_page', l: 'Artworks page', route: '/artworks' },
    { k: 'exhibitions_page', l: 'Exhibitions page', route: '/exhibitions' },
    { k: 'journal_page', l: 'Journal page', route: '/journal' },
    { k: 'film_page', l: 'Film page', route: '/film' },
    { k: 'contact', l: 'Contact', route: '/contact' },
];

const fmtAgo = (iso) => {
    if (!iso) return 'Never edited';
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'Edited just now';
    if (min < 60) return `Edited ${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `Edited ${hr}h ago`;
    return `Edited ${new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`;
};

export default function SiteSettings() {
    const [data, setData] = useState(null);
    const [original, setOriginal] = useState(null);
    const [tab, setTab] = useState('home');
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState('');
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        getSiteSettings().then((d) => { setData(d); setOriginal(JSON.parse(JSON.stringify(d))); });
        return () => clearPreviewSettings();
    }, []);

    /* Live push to preview tab whenever the form changes (if user opened a preview window) */
    useEffect(() => {
        if (data && dirty) pushPreviewSettings(data);
    }, [data, dirty]);

    const setSection = (k, v) => {
        setData((d) => ({ ...d, [k]: { ...d[k], ...v } }));
        setDirty(true);
    };

    const save = async () => {
        setSaving(true); setMsg('');
        try {
            const fresh = await updateSiteSettings(data);
            setData(fresh);
            setOriginal(JSON.parse(JSON.stringify(fresh)));
            setDirty(false);
            clearPreviewSettings();
            invalidateSettings();
            setMsg('Saved. Refresh any open public tabs to see the change.');
        } catch (e) {
            setMsg(e.response?.data?.detail || 'Save failed.');
        } finally { setSaving(false); }
    };

    const reset = async () => {
        if (!window.confirm('Restore default content for every section? This cannot be undone.')) return;
        setSaving(true);
        try {
            const fresh = await resetSiteSettings();
            setData(fresh);
            setOriginal(JSON.parse(JSON.stringify(fresh)));
            setDirty(false);
            clearPreviewSettings();
            invalidateSettings();
            setMsg('Defaults restored.');
        } finally { setSaving(false); }
    };

    const openPreview = () => {
        pushPreviewSettings(data);
        const t = TABS.find((x) => x.k === tab);
        const url = `${window.location.origin}${t.route}?preview=1`;
        window.open(url, 'mt-preview');
    };

    const discardChanges = () => {
        if (!dirty) return;
        if (!window.confirm('Discard all unsaved changes in this session?')) return;
        setData(JSON.parse(JSON.stringify(original)));
        setDirty(false);
        clearPreviewSettings();
        setMsg('Unsaved changes discarded.');
    };

    if (!data) {
        return <p className="caption" style={{ fontSize: 18, color: 'var(--ink-2)' }}>Loading…</p>;
    }

    const lastEdited = data._last_edited || {};

    return (
        <div className="flex flex-col gap-10" data-testid="site-settings-page">
            <header className="flex flex-col gap-3">
                <span className="label" style={{ color: 'var(--clay)' }}>§ Site Settings</span>
                <h1 className="serif" style={{ fontSize: 42, letterSpacing: '-0.02em' }}>Editorial content</h1>
                <p className="caption" style={{ fontSize: 17, lineHeight: 1.55, color: 'var(--ink-2)', maxWidth: 700 }}>
                    Edit the static editorial copy and imagery on the Home, About, Private Viewing and Contact pages. Artists, artworks, exhibitions and journal posts continue to live in their own sections.
                </p>
            </header>

            {/* Tabs */}
            <div className="flex flex-wrap items-end gap-x-6 gap-y-3 rule-bottom pb-4">
                {TABS.map((t) => (
                    <button
                        key={t.k}
                        data-testid={`settings-tab-${t.k}`}
                        onClick={() => setTab(t.k)}
                        className="label"
                        style={{
                            color: tab === t.k ? 'var(--clay)' : 'var(--ink-2)',
                            paddingBottom: 4,
                            background: 'transparent',
                            borderTopWidth: 0,
                            borderLeftWidth: 0,
                            borderRightWidth: 0,
                            borderBottomWidth: 1,
                            borderBottomStyle: 'solid',
                            borderBottomColor: tab === t.k ? 'var(--clay)' : 'transparent',
                            cursor: 'pointer',
                        }}
                    >
                        {t.l}
                    </button>
                ))}
                <span
                    data-testid={`last-edited-${tab}`}
                    className="caption"
                    style={{
                        marginLeft: 'auto',
                        fontSize: 13,
                        color: 'var(--ink-3)',
                        fontStyle: 'italic',
                    }}
                >
                    {fmtAgo(lastEdited[tab])}
                    {dirty && <span style={{ marginLeft: 14, color: 'var(--clay-deep)', fontStyle: 'normal', fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase' }}>· Unsaved</span>}
                </span>
            </div>

            {/* BRAND */}
            {tab === 'brand' && data.brand && (
                <div className="flex flex-col gap-16" data-testid="settings-brand">
                    <Section title="Logos">
                        <p className="caption" style={{ fontSize: 15, color: 'var(--ink-3)', marginTop: -4, marginBottom: 8 }}>
                            Upload <em>SVG</em> for crispness at any size. PNG with transparent background also works. Leave any slot empty to fall back to the typographic wordmark.
                        </p>
                        <Field label="Primary logo — light version" hint="For dark backgrounds (top of hero, scrolled-up nav over dark heroes).">
                            <ImageUploader value={data.brand.logo_primary_light} onChange={(v) => setSection('brand', { logo_primary_light: v })} testid="brand-logo-light" />
                        </Field>
                        <Field label="Primary logo — dark version" hint="For light backgrounds (scrolled cream nav, footer, admin sign-in, emails).">
                            <ImageUploader value={data.brand.logo_primary_dark} onChange={(v) => setSection('brand', { logo_primary_dark: v })} testid="brand-logo-dark" />
                        </Field>
                        <Field label="Secondary / monogram (optional)" hint="A short mark — used for favicons or compact placements when supplied.">
                            <ImageUploader value={data.brand.logo_secondary} onChange={(v) => setSection('brand', { logo_secondary: v })} testid="brand-logo-secondary" />
                        </Field>
                    </Section>

                    <Section title="Browser tab">
                        <Field label="Favicon" hint="SVG, ICO or PNG. 32×32 or 48×48 ideal for raster; SVG scales perfectly.">
                            <ImageUploader value={data.brand.favicon} onChange={(v) => setSection('brand', { favicon: v })} testid="brand-favicon" />
                        </Field>
                        <Field label="Browser tab title (default)" hint="Shown when a page doesn't supply its own title.">
                            <TextInput value={data.brand.browser_title || ''} onChange={(e) => setSection('brand', { browser_title: e.target.value })} />
                        </Field>
                    </Section>

                    <Section title="Search & social sharing">
                        <Field label="Meta description (default)" hint="One or two sentences. Used for search results and social previews when a page doesn't supply its own.">
                            <TextArea rows={3} value={data.brand.meta_description || ''} onChange={(e) => setSection('brand', { meta_description: e.target.value })} />
                        </Field>
                        <Field label="Open Graph image" hint="Shown when the site is shared on social — recommended 1200×630, JPG or PNG.">
                            <ImageUploader value={data.brand.og_image} onChange={(v) => setSection('brand', { og_image: v })} testid="brand-og-image" />
                        </Field>
                    </Section>
                </div>
            )}

            {/* HOME */}
            {tab === 'home' && (
                <div className="flex flex-col gap-16" data-testid="settings-home">
                    <Section title="Hero">
                        <Field label="Hero image"><ImageUploader value={data.home.hero_image} onChange={(v) => setSection('home', { hero_image: v })} testid="home-hero-image" /></Field>
                        <Field label="Hero image credit (bottom-right of hero)" hint="e.g. Amara Okafor · Soft Architecture III, 2024"><TextInput value={data.home.hero_image_credit || ''} onChange={(e) => setSection('home', { hero_image_credit: e.target.value })} /></Field>
                        <Row>
                            <Field label="Headline — line 1"><TextInput value={data.home.hero_headline_line1 || ''} onChange={(e) => setSection('home', { hero_headline_line1: e.target.value })} /></Field>
                            <Field label="Headline — italic word" hint="rendered in italic on line 2"><TextInput value={data.home.hero_headline_italic || ''} onChange={(e) => setSection('home', { hero_headline_italic: e.target.value })} /></Field>
                            <Field label="Headline — line 3"><TextInput value={data.home.hero_headline_line3 || ''} onChange={(e) => setSection('home', { hero_headline_line3: e.target.value })} /></Field>
                        </Row>
                        <Field label="Subtitle paragraph"><TextArea rows={3} value={data.home.hero_subtitle || ''} onChange={(e) => setSection('home', { hero_subtitle: e.target.value })} /></Field>
                        <Row>
                            <Field label="Primary CTA label"><TextInput value={data.home.hero_cta_primary_label || ''} onChange={(e) => setSection('home', { hero_cta_primary_label: e.target.value })} /></Field>
                            <Field label="Primary CTA link" hint="e.g. /artworks"><TextInput value={data.home.hero_cta_primary_link || ''} onChange={(e) => setSection('home', { hero_cta_primary_link: e.target.value })} /></Field>
                            <Field label="Secondary CTA label"><TextInput value={data.home.hero_cta_secondary_label || ''} onChange={(e) => setSection('home', { hero_cta_secondary_label: e.target.value })} /></Field>
                            <Field label="Secondary CTA link"><TextInput value={data.home.hero_cta_secondary_link || ''} onChange={(e) => setSection('home', { hero_cta_secondary_link: e.target.value })} /></Field>
                        </Row>
                    </Section>

                    <Section title="Statement (centered editorial line)">
                        <Field label="Overline"><TextInput value={data.home.statement_overline || ''} onChange={(e) => setSection('home', { statement_overline: e.target.value })} /></Field>
                        <Field label="Statement text"><TextArea rows={3} value={data.home.statement_text || ''} onChange={(e) => setSection('home', { statement_text: e.target.value })} /></Field>
                    </Section>

                    <Section title="Founder block">
                        <Field label="Overline"><TextInput value={data.home.founder_overline || ''} onChange={(e) => setSection('home', { founder_overline: e.target.value })} /></Field>
                        <Row>
                            <Field label="Heading"><TextInput value={data.home.founder_heading || ''} onChange={(e) => setSection('home', { founder_heading: e.target.value })} /></Field>
                            <Field label="Heading — italic part"><TextInput value={data.home.founder_heading_italic || ''} onChange={(e) => setSection('home', { founder_heading_italic: e.target.value })} /></Field>
                        </Row>
                        <Field label="Body paragraph"><TextArea rows={4} value={data.home.founder_body || ''} onChange={(e) => setSection('home', { founder_body: e.target.value })} /></Field>
                        <Field label="Pull-quote"><TextArea rows={3} value={data.home.founder_quote || ''} onChange={(e) => setSection('home', { founder_quote: e.target.value })} /></Field>
                        <Field label="Founder image (or gallery image)"><ImageUploader value={data.home.founder_image} onChange={(v) => setSection('home', { founder_image: v })} testid="home-founder-image" /></Field>
                    </Section>

                    <Section title="Collector's Care services (three columns)">
                        <Field label="Overline"><TextInput value={data.home.services_overline || ''} onChange={(e) => setSection('home', { services_overline: e.target.value })} /></Field>
                        <Row>
                            <Field label="Heading"><TextInput value={data.home.services_heading || ''} onChange={(e) => setSection('home', { services_heading: e.target.value })} /></Field>
                            <Field label="Heading — italic part"><TextInput value={data.home.services_heading_italic || ''} onChange={(e) => setSection('home', { services_heading_italic: e.target.value })} /></Field>
                        </Row>
                        {(data.home.services || []).map((s, i) => (
                            <div key={i} className="rule-top pt-6 flex flex-col gap-5">
                                <span className="label" style={{ color: 'var(--clay)' }}>Service {s.n}</span>
                                <Row>
                                    <Field label="Marker (e.g. I, II, III)"><TextInput value={s.n} onChange={(e) => setSection('home', { services: data.home.services.map((x, j) => j === i ? { ...x, n: e.target.value } : x) })} /></Field>
                                    <Field label="Title"><TextInput value={s.title} onChange={(e) => setSection('home', { services: data.home.services.map((x, j) => j === i ? { ...x, title: e.target.value } : x) })} /></Field>
                                </Row>
                                <Field label="Description"><TextArea rows={3} value={s.description} onChange={(e) => setSection('home', { services: data.home.services.map((x, j) => j === i ? { ...x, description: e.target.value } : x) })} /></Field>
                            </div>
                        ))}
                    </Section>
                </div>
            )}

            {/* ABOUT */}
            {tab === 'about' && (
                <div className="flex flex-col gap-16" data-testid="settings-about">
                    <Section title="Hero headline">
                        <Field label="Overline"><TextInput value={data.about.overline || ''} onChange={(e) => setSection('about', { overline: e.target.value })} /></Field>
                        <Row>
                            <Field label="Headline — part 1"><TextInput value={data.about.headline_part1 || ''} onChange={(e) => setSection('about', { headline_part1: e.target.value })} /></Field>
                            <Field label="Headline — italic part" hint="rendered in italic mid-sentence"><TextInput value={data.about.headline_italic || ''} onChange={(e) => setSection('about', { headline_italic: e.target.value })} /></Field>
                            <Field label="Headline — part 2"><TextInput value={data.about.headline_part2 || ''} onChange={(e) => setSection('about', { headline_part2: e.target.value })} /></Field>
                        </Row>
                    </Section>

                    <Section title="Founder block">
                        <Field label="Founder image"><ImageUploader value={data.about.founder_image} onChange={(v) => setSection('about', { founder_image: v })} testid="about-founder-image" /></Field>
                        <Field label="Overline"><TextInput value={data.about.founder_overline || ''} onChange={(e) => setSection('about', { founder_overline: e.target.value })} /></Field>
                        <Field label="Founder name (with trailing punctuation, e.g. 'Fatym Touré.')"><TextInput value={data.about.founder_name || ''} onChange={(e) => setSection('about', { founder_name: e.target.value })} /></Field>
                        <Field label="Paragraph 1"><TextArea rows={4} value={data.about.founder_body_1 || ''} onChange={(e) => setSection('about', { founder_body_1: e.target.value })} /></Field>
                        <Field label="Paragraph 2 (optional)"><TextArea rows={4} value={data.about.founder_body_2 || ''} onChange={(e) => setSection('about', { founder_body_2: e.target.value })} /></Field>
                    </Section>

                    <Section title="Principles (list)">
                        <Field label="Overline"><TextInput value={data.about.principles_overline || ''} onChange={(e) => setSection('about', { principles_overline: e.target.value })} /></Field>
                        {(data.about.principles || []).map((p, i) => (
                            <div key={i} className="rule-top pt-6 flex flex-col gap-5">
                                <span className="label" style={{ color: 'var(--clay)' }}>Principle {p.n}</span>
                                <Row>
                                    <Field label="Marker"><TextInput value={p.n} onChange={(e) => setSection('about', { principles: data.about.principles.map((x, j) => j === i ? { ...x, n: e.target.value } : x) })} /></Field>
                                    <Field label="Title"><TextInput value={p.title} onChange={(e) => setSection('about', { principles: data.about.principles.map((x, j) => j === i ? { ...x, title: e.target.value } : x) })} /></Field>
                                </Row>
                                <Field label="Description"><TextArea rows={3} value={p.description} onChange={(e) => setSection('about', { principles: data.about.principles.map((x, j) => j === i ? { ...x, description: e.target.value } : x) })} /></Field>
                                <button type="button" onClick={() => setSection('about', { principles: data.about.principles.filter((_, j) => j !== i) })} className="label" style={{ color: 'var(--clay-deep)', alignSelf: 'flex-start' }}>Remove this principle</button>
                            </div>
                        ))}
                        <button type="button" onClick={() => setSection('about', { principles: [...(data.about.principles || []), { n: '', title: '', description: '' }] })} className="btn-ghost self-start">+ Add a principle</button>
                    </Section>
                </div>
            )}

            {/* PRIVATE VIEWING */}
            {tab === 'private_viewing' && (
                <div className="flex flex-col gap-16" data-testid="settings-private-viewing">
                    <Section title="Hero">
                        <Field label="Hero image"><ImageUploader value={data.private_viewing.hero_image} onChange={(v) => setSection('private_viewing', { hero_image: v })} testid="pv-hero-image" /></Field>
                        <Field label="Overline"><TextInput value={data.private_viewing.hero_overline || ''} onChange={(e) => setSection('private_viewing', { hero_overline: e.target.value })} /></Field>
                        <Row>
                            <Field label="Headline"><TextInput value={data.private_viewing.hero_headline || ''} onChange={(e) => setSection('private_viewing', { hero_headline: e.target.value })} /></Field>
                            <Field label="Headline — italic part"><TextInput value={data.private_viewing.hero_headline_italic || ''} onChange={(e) => setSection('private_viewing', { hero_headline_italic: e.target.value })} /></Field>
                        </Row>
                    </Section>
                    <Section title="Invitation">
                        <Field label="Overline"><TextInput value={data.private_viewing.invitation_overline || ''} onChange={(e) => setSection('private_viewing', { invitation_overline: e.target.value })} /></Field>
                        <Field label="Heading"><TextInput value={data.private_viewing.invitation_heading || ''} onChange={(e) => setSection('private_viewing', { invitation_heading: e.target.value })} /></Field>
                        <Field label="Paragraph 1"><TextArea rows={4} value={data.private_viewing.invitation_body_1 || ''} onChange={(e) => setSection('private_viewing', { invitation_body_1: e.target.value })} /></Field>
                        <Field label="Paragraph 2 (optional)"><TextArea rows={4} value={data.private_viewing.invitation_body_2 || ''} onChange={(e) => setSection('private_viewing', { invitation_body_2: e.target.value })} /></Field>
                    </Section>
                </div>
            )}

            {/* ---- Simple hero tabs (Artists, Artworks, Exhibitions, Journal, Film) ---- */}
            {['artists_page', 'artworks_page', 'exhibitions_page', 'journal_page', 'film_page'].includes(tab) && data[tab] && (
                <div className="flex flex-col gap-16" data-testid={`settings-${tab}`}>
                    <Section title="Hero">
                        <p className="caption" style={{ fontSize: 15, color: 'var(--ink-3)', marginTop: -4, marginBottom: 8 }}>
                            The overline, main title and introductory paragraph shown at the top of this page.
                        </p>
                        <Field label="Overline" hint="Small tracked-caps label above the title.">
                            <TextInput
                                data-testid={`${tab}-hero-overline`}
                                value={data[tab].hero_overline || ''}
                                onChange={(e) => setSection(tab, { hero_overline: e.target.value })}
                            />
                        </Field>
                        <Field label="Main title" hint="Rendered in italic display. Include trailing punctuation if desired (e.g. Artists.).">
                            <TextInput
                                data-testid={`${tab}-hero-title`}
                                value={data[tab].hero_title || ''}
                                onChange={(e) => setSection(tab, { hero_title: e.target.value })}
                            />
                        </Field>
                        <Field label="Introductory paragraph" hint="Editorial paragraph shown beneath the title. Leave empty to hide.">
                            <TextArea
                                rows={4}
                                data-testid={`${tab}-hero-intro`}
                                value={data[tab].hero_intro || ''}
                                onChange={(e) => setSection(tab, { hero_intro: e.target.value })}
                            />
                        </Field>
                    </Section>

                    {/* Film-page-only: editable list of video categories.
                        Rendered as a plain textarea (one category per line) to
                        keep the CMS visually consistent with every other list
                        field on this page — no new components, no design shift. */}
                    {tab === 'film_page' && (
                        <Section title="Categories">
                            <p className="caption" style={{ fontSize: 15, color: 'var(--ink-3)', marginTop: -4, marginBottom: 8 }}>
                                One category per line, in the order they should appear as filter tabs on the public Film page and as options in the video editor. Existing videos are not modified when you rename or remove a category; only the tabs and dropdown reflect the change.
                            </p>
                            <Field label="Categories">
                                <TextArea
                                    rows={7}
                                    data-testid="film-page-categories"
                                    value={(data.film_page.categories || []).join('\n')}
                                    onChange={(e) => {
                                        const lines = e.target.value
                                            .split('\n')
                                            .map((s) => s.trim())
                                            .filter(Boolean);
                                        setSection('film_page', { categories: lines });
                                    }}
                                />
                            </Field>
                        </Section>
                    )}
                </div>
            )}

            {/* CONTACT */}
            {tab === 'contact' && (
                <div className="flex flex-col gap-16" data-testid="settings-contact">
                    <Section title="Hero">
                        <Field label="Overline"><TextInput value={data.contact.hero_overline || ''} onChange={(e) => setSection('contact', { hero_overline: e.target.value })} /></Field>
                        <Field label="Main title" hint="Rendered in italic display."><TextInput value={data.contact.hero_title || ''} onChange={(e) => setSection('contact', { hero_title: e.target.value })} /></Field>
                        <Field label="Introductory paragraph" hint="Leave empty to hide."><TextArea rows={4} value={data.contact.hero_intro || ''} onChange={(e) => setSection('contact', { hero_intro: e.target.value })} /></Field>
                    </Section>
                    <Section title="Visit (address block)">
                        <Field label="Line 1"><TextInput value={data.contact.address_line_1 || ''} onChange={(e) => setSection('contact', { address_line_1: e.target.value })} /></Field>
                        <Field label="Line 2"><TextInput value={data.contact.address_line_2 || ''} onChange={(e) => setSection('contact', { address_line_2: e.target.value })} /></Field>
                        <Field label="Line 3 (italic line)"><TextInput value={data.contact.address_line_3 || ''} onChange={(e) => setSection('contact', { address_line_3: e.target.value })} /></Field>
                    </Section>
                    <Section title="Write">
                        <Row>
                            <Field label="Primary email"><TextInput value={data.contact.email_primary || ''} onChange={(e) => setSection('contact', { email_primary: e.target.value })} /></Field>
                            <Field label="Secondary email (optional)"><TextInput value={data.contact.email_secondary || ''} onChange={(e) => setSection('contact', { email_secondary: e.target.value })} /></Field>
                        </Row>
                    </Section>
                    <Section title="Call">
                        <Field label="Phone"><TextInput value={data.contact.phone || ''} onChange={(e) => setSection('contact', { phone: e.target.value })} /></Field>
                    </Section>
                    <Section title="Follow">
                        <Row>
                            <Field label="Instagram URL"><TextInput value={data.contact.instagram_url || ''} onChange={(e) => setSection('contact', { instagram_url: e.target.value })} /></Field>
                            <Field label="Instagram handle (display only)"><TextInput value={data.contact.instagram_handle || ''} onChange={(e) => setSection('contact', { instagram_handle: e.target.value })} /></Field>
                        </Row>
                    </Section>
                </div>
            )}

            {/* Save bar */}
            <div className="sticky bottom-0 rule-top py-6 flex flex-wrap items-center gap-x-8 gap-y-3" style={{ background: 'var(--paper)' }}>
                <button data-testid="settings-save" onClick={save} disabled={saving || !dirty} className="btn-primary">
                    {saving ? 'Saving…' : (dirty ? 'Save changes' : 'No changes')}
                </button>
                <button data-testid="settings-preview" onClick={openPreview} className="btn-ghost">
                    Open preview
                </button>
                {dirty && (
                    <button onClick={discardChanges} className="label" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-2)' }}>
                        Discard changes
                    </button>
                )}
                <button onClick={reset} disabled={saving} className="label" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--clay-deep)', marginLeft: 'auto' }}>
                    Restore defaults
                </button>
                {msg && <span className="caption" style={{ fontSize: 14, color: 'var(--ink-2)', flexBasis: '100%' }}>{msg}</span>}
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section className="flex flex-col gap-7 rule-bottom pb-12">
            <h2 className="serif-italic" style={{ fontSize: 26, letterSpacing: '-0.01em' }}>{title}</h2>
            {children}
        </section>
    );
}

function Row({ children }) {
    return <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">{children}</div>;
}
